
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/use-toast';
import { Upload, Download, AlertCircle, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AccountFinal } from '@/api/entities';
import { Progress } from '@/components/ui/progress';

export default function BVOUploader() {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);
  const { toast } = useToast();

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setUploadResults(null);
    } else {
      toast({
        title: 'Invalid file',
        description: 'Please select a CSV file',
        variant: 'destructive'
      });
    }
  };

  const downloadTemplate = () => {
    const csv = 'account_id,bvo_label,bvo_rationale\nau_001_001,Budget-Driven,"This institution prioritizes cost efficiency and seeks budget-friendly solutions with clear ROI. They typically require detailed financial justifications and prefer vendors who can demonstrate cost savings and operational efficiencies."\nau_001_002,Value-Seeker,"Focuses on getting the best value for their investment, balancing cost with quality and long-term benefits."';
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bvo_upload_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  };

  const handleUpload = async () => {
    if (!file) {
      toast({
        title: 'No file selected',
        description: 'Please select a CSV file to upload',
        variant: 'destructive'
      });
      return;
    }

    setIsUploading(true);
    setProgress(0);
    setUploadResults(null);

    try {
      // Read and parse CSV
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      // Parse CSV header
      const headers = lines[0].split(',').map(h => h.trim());
      
      // Validate headers
      if (headers[0] !== 'account_id' || headers[1] !== 'bvo_label' || headers[2] !== 'bvo_rationale') {
        throw new Error('CSV must have columns: account_id, bvo_label, bvo_rationale (in that order)');
      }

      // Parse CSV rows
      const parseCSVLine = (line) => {
        const result = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            result.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        result.push(current.trim());
        return result;
      };

      // Get all accounts
      const allAccounts = await AccountFinal.list();
      const accountMap = new Map(allAccounts.map(acc => [acc.account_id || acc.id, acc]));

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      toast({
        title: 'Processing...',
        description: `Updating ${lines.length - 1} accounts with Digital Investment Capacity data`,
        duration: 10000
      });

      // Process each row
      for (let i = 1; i < lines.length; i++) {
        try {
          const values = parseCSVLine(lines[i]);
          const accountId = values[0]?.trim();
          const bvoLabel = values[1]?.trim();
          const bvoRationale = values[2]?.trim();

          if (!accountId) {
            console.warn(`Row ${i + 1}: Missing account_id, skipping`);
            errorCount++;
            errors.push(`Row ${i + 1}: Missing account_id`);
            continue;
          }

          // Validate rationale length
          if (bvoRationale && bvoRationale.length > 350) {
            console.warn(`Row ${i + 1}: bvo_rationale exceeds 350 characters (${bvoRationale.length} chars)`);
            errorCount++;
            errors.push(`Row ${i + 1}: Rationale too long (${bvoRationale.length} chars, max 350)`);
            continue;
          }

          // Find matching account
          const account = accountMap.get(accountId);
          if (!account) {
            console.warn(`Row ${i + 1}: Account not found with ID: ${accountId}`);
            errorCount++;
            errors.push(`Row ${i + 1}: Account not found (${accountId})`);
            continue;
          }

          // Update account
          await AccountFinal.update(account.id, {
            bvo_label: bvoLabel || null,
            bvo_rationale: bvoRationale || null
          });

          successCount++;

          // Update progress
          setProgress(Math.round((i / (lines.length - 1)) * 100));

          // Add delay to avoid rate limiting
          if (i < lines.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 700));
          }

          // Progress update every 10 records
          if (successCount % 10 === 0) {
            toast({
              title: 'Progress...',
              description: `Updated ${successCount} of ${lines.length - 1} accounts`,
              duration: 3000
            });
          }
        } catch (error) {
          console.error(`Error processing row ${i}:`, error);
          errorCount++;
          errors.push(`Row ${i + 1}: ${error.message}`);
          
          // Handle rate limiting
          if (error.response?.status === 429) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
      }

      setFile(null);
      setProgress(100);
      setUploadResults({ successCount, errorCount, errors });
      
      if (errorCount === 0) {
        toast({
          title: 'Success!',
          description: `Updated ${successCount} accounts with Digital Investment Capacity data`,
          duration: 5000
        });
      } else {
        toast({
          title: 'Partially completed',
          description: `${successCount} succeeded, ${errorCount} failed`,
          variant: 'default',
          duration: 7000
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'An error occurred during upload',
        variant: 'destructive'
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Digital Investment Capacity Uploader
        </CardTitle>
        <CardDescription>
          Upload Digital Investment Capacity labels and rationales for accounts
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>CSV Format:</strong>
            <br />
            • Column 1: <code className="bg-gray-100 px-1 rounded">account_id</code> (exact match required)
            <br />
            • Column 2: <code className="bg-gray-100 px-1 rounded">bvo_label</code> (e.g., "Budget-Driven", "Value-Seeker", "Outcome-Focused")
            <br />
            • Column 3: <code className="bg-gray-100 px-1 rounded">bvo_rationale</code> (max 350 characters, use quotes if contains commas)
            <br />
            <br />
            <strong>Example:</strong>
            <br />
            <code className="bg-gray-100 px-1 rounded text-xs">
              au_001_001,Budget-Driven,"This institution prioritizes cost efficiency..."
            </code>
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <Label>Download Template</Label>
          <Button onClick={downloadTemplate} variant="outline" className="w-full">
            <Download className="w-4 h-4 mr-2" />
            Download CSV Template
          </Button>
        </div>

        <div className="space-y-2">
          <Label htmlFor="file">Upload CSV</Label>
          <Input
            id="file"
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            disabled={isUploading}
          />
          {file && (
            <p className="text-sm text-gray-600">
              Selected: {file.name}
            </p>
          )}
        </div>

        {isUploading && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Uploading...</span>
              <span>{progress}%</span>
            </div>
            <Progress value={progress} className="w-full" />
          </div>
        )}

        {uploadResults && (
          <Alert className={uploadResults.errorCount === 0 ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'}>
            {uploadResults.errorCount === 0 ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : (
              <AlertCircle className="h-4 w-4 text-yellow-600" />
            )}
            <AlertDescription>
              <strong>Upload Complete:</strong>
              <br />
              ✅ {uploadResults.successCount} accounts updated successfully
              {uploadResults.errorCount > 0 && (
                <>
                  <br />
                  ❌ {uploadResults.errorCount} errors
                  {uploadResults.errors.length > 0 && (
                    <div className="mt-2 max-h-40 overflow-y-auto">
                      <details className="text-xs">
                        <summary className="cursor-pointer font-medium">View errors</summary>
                        <ul className="mt-2 space-y-1 list-disc list-inside">
                          {uploadResults.errors.slice(0, 20).map((error, idx) => (
                            <li key={idx}>{error}</li>
                          ))}
                          {uploadResults.errors.length > 20 && (
                            <li>... and {uploadResults.errors.length - 20} more</li>
                          )}
                        </ul>
                      </details>
                    </div>
                  )}
                </>
              )}
            </AlertDescription>
          </Alert>
        )}

        <Button
          onClick={handleUpload}
          disabled={!file || isUploading}
          className="w-full"
        >
          {isUploading ? (
            'Uploading...'
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Upload Digital Investment Capacity Data
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
